/**
 * React Query keys for the Users domain.
 *
 * The client omits falsy filters before sending, so `{ search: '' }` and `{}`
 * produce the same HTTP request. Normalizing the key the same way keeps them on
 * one cache entry instead of firing a duplicate request, and sorting the role
 * set means picking DSO then Finance hits the same entry as the reverse.
 */
const normalizeInternalUserFilters = (filters) => {
  const out = {};
  if (filters?.search) out.search = filters.search;
  if (filters?.roles?.length) out.roles = [...filters.roles].sort();

  return out;
};

export const usersKeys = {
  all: ['users'],
  // Matches every page and filter combination of the GoTu Admin list.
  // Invalidation code should use this rather than hand-writing the literal.
  internalUsersPrefix: () => ['users', 'internal-users'],
  internalUsers: (filters, page) => [
    ...usersKeys.internalUsersPrefix(),
    normalizeInternalUserFilters(filters),
    page,
  ],
  // A sibling of the list prefix, not a member of it. `/me` answers a different
  // question about a different route, and nesting it would make every
  // invalidation of the GoTu Admin list refetch the signed-in admin's role too.
  internalUserMe: () => ['users', 'internal-user-me'],
  // Every admin, unpaged, as the Sales Metros page names and assigns them. A
  // sibling of the list prefix for the same reason as `/me`: it is a different
  // read of the endpoint, and nothing that invalidates one page of the GoTu
  // Admin list needs to refetch the whole set.
  allInternalUsers: () => ['users', 'all-internal-users'],
  // One user's NPS answers, as the profile's NPS tab lists them.
  npsReviews: (userId) => ['users', 'nps-reviews', userId],
  // A page of the Location Users list, keyed on the params actually sent (see
  // `toUserSearchParams`) rather than on the typed text, so two spellings of
  // one phone number share an entry, and no search is `{}` either way.
  locationUsers: (searchParams, page) => ['users', 'location-users', searchParams, page],
  // A sibling of the list, not a member: the counts come from another service
  // and are keyed on the page's ids, not on its search.
  identityLocationCounts: (identityIds) => ['users', 'identity-location-counts', identityIds],
  // One identity, as the Location User profile shows and edits it.
  locationUser: (identityId) => ['users', 'location-user', identityId],
};
